import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './SendDialog.css';
import apiService from '../../services/api';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';

const SendDialog = ({
  project,
  contacts,          // Customer (Account) names
  emails,
  surveys,
  customerContactIds,
  onClose,
  onSave
}) => {
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [surveyUniqueIds, setSurveyUniqueIds] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const dropdownRef = useRef(null);
  const accessToken = Cookies.get('access_token');
  const surveyBaseUrl = window._env_.REACT_APP_SURVEY_BASE_URL;
  //const surveyBaseUrl = process.env.REACT_APP_SURVEY_BASE_URL;
  const userId = sessionStorage.getItem('user_id');

  // Safe arrays
  const safeCustomers = Array.isArray(contacts) ? contacts : [];
  const safeEmails = Array.isArray(emails) ? emails : [];
  const safeCustomerContactIds = Array.isArray(customerContactIds) ? customerContactIds : [];

  // Map: customer → { email, contactId }
  const customerMap = safeCustomers.reduce((map, customer, index) => {
    const email = safeEmails[index] || '';
    const contactId = safeCustomerContactIds[index] || '';
    map[customer] = { email, contactId };
    return map;
  }, {});

  // === 1. Generate unique IDs ONCE when dialog opens ===
  useEffect(() => {
    const persistentIds = {};
    safeCustomers.forEach(customer => {
      persistentIds[customer] = uuidv4().replace(/-/g, '');
    });
    setSurveyUniqueIds(persistentIds);
  }, [safeCustomers]);

  // === 2. SET SUBJECT: "We’d love to hear from you about {{ProjectName}}" ===
  useEffect(() => {
    if (!selectedSurveyId || Object.keys(surveyUniqueIds).length === 0) {
      setSubject('');
      setBody('');
      return;
    }

    const selectedSurvey = surveys.find(s => s.SurveyID === parseInt(selectedSurveyId));
    if (!selectedSurvey) return;

    const projectName = project?.['Project Name'] || 'Unknown Project';

    // EXACT SUBJECT LINE
    setSubject(`We’d love to hear from you about ${projectName}`);

    // Build stable body preview
    const links = selectedCustomers
      .map(customer => {
        const { email } = customerMap[customer] || {};
        const uid = surveyUniqueIds[customer];
        return `${customer} (${email || 'No email'}): ${surveyBaseUrl}${uid}`;
      })
      .join('\n');

    setBody(links || 'No customers selected.');
  }, [selectedSurveyId, selectedCustomers, surveyUniqueIds, surveyBaseUrl, customerMap, project, surveys]);

  // === 3. Close dropdown on outside click ===
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        const menu = dropdownRef.current?.querySelector('.custom-dropdown-menu');
        menu?.classList.remove('active');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // === 4. Toggle customer selection ===
  const toggleCustomer = (customer) => {
    setSelectedCustomers(prev => {
      const newSel = prev.includes(customer)
        ? prev.filter(c => c !== customer)
        : [...prev, customer];
      setSelectAll(newSel.length === safeCustomers.length);
      return newSel;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCustomers([]);
      setSelectAll(false);
    } else {
      setSelectedCustomers([...safeCustomers]);
      setSelectAll(true);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(p => !p);
    const menu = dropdownRef.current?.querySelector('.custom-dropdown-menu');
    menu?.classList.toggle('active');
    setSearchTerm('');
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  // Filter by customer name or email
  const filteredCustomers = safeCustomers.filter(customer => {
    const { email } = customerMap[customer] || {};
    const lower = searchTerm.toLowerCase();
    return (
      customer.toLowerCase().includes(lower) ||
      (email && email.toLowerCase().includes(lower))
    );
  });

  // === 5. Fetch & format sent surveys ===
  const fetchAndFormatData = async () => {
    const [sentRes, statsRes] = await Promise.all([
      apiService.fetchData('projects/surveysent'),
      apiService.fetchData('projects/statistics')
    ]);

    const formattedSurveys = Array.isArray(sentRes.results)
      ? sentRes.results.map((s, i) => {
          const date = new Date(s.SentOn);
          const fd = isNaN(date.getTime())
            ? 'N/A'
            : `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear().toString().slice(2)}`;
          return {
            id: i + 1,
            surveyName: s.SurveyName || 'N/A',
            project: s.ProjectName || 'N/A',
            account: s.Account || 'N/A',
            sentDate: fd,
            responseStatus: s.ResponseStatus || 'Not Responded',
            counter: s.EmailCounter || 1,
            email: s.Email || 'N/A',
            contactId: s.ContactId || null,
            surveyId: s.SurveyId || null,
            projectId: s.ProjectID || null,
            UniqueID: s.UniqueID || ''
          };
        })
      : [];

    const updatedStatistics = statsRes.results?.[0] || null;
    return { formattedSurveys, updatedStatistics };
  };

  // === 6. Send emails & log surveys ===
  const handleSave = async () => {
    onClose();

    if (selectedCustomers.length === 0 || !selectedSurveyId) {
      alert('Please select at least one customer and a survey.');
      return;
    }

    const selectedSurvey = surveys.find(s => s.SurveyID === parseInt(selectedSurveyId));
    const emailPayloads = [];
    const surveyPayloads = [];

    for (const customer of selectedCustomers) {
      const { email: sendTo, contactId } = customerMap[customer] || {};
      const uniqueId = surveyUniqueIds[customer];

      if (!sendTo) {
        alert(`No email for customer: ${customer}`);
        continue;
      }
      if (!contactId) {
        alert(`No contact ID for customer: ${customer}`);
        continue;
      }

      const surveyLink = `${surveyBaseUrl}${uniqueId}`;

      emailPayloads.push({
        endpoint: 'send_email',
        customerName: customer,
        surveyLink,
        surveyType: selectedSurvey?.Type || 'N/A',
        projectName: project?.['Project Name'] || 'Unknown Project',
        send_to: sendTo,
        subject,
        token: accessToken,
        templateType: 'survey'
      });

      surveyPayloads.push({
        SurveyID: parseInt(selectedSurveyId),
        ProjectID: project?.id || 1,
        ContactID: parseInt(contactId),
        SentBy: parseInt(userId) || 1,
        Subject: subject,
        Body: surveyLink,
        EmailSent: 0,
        UniqueID: uniqueId
      });
    }

    if (emailPayloads.length === 0) {
      alert('No valid customers selected.');
      return;
    }

    try {
      for (let i = 0; i < emailPayloads.length; i++) {
        const ep = emailPayloads[i];
        const sp = surveyPayloads[i];

        try {
          await apiService.sendEmail(
            ep.endpoint,
            ep.customerName,
            ep.surveyLink,
            ep.surveyType,
            ep.projectName,
            ep.send_to,
            ep.subject,
            ep.token,
            ep.templateType
          );
          sp.EmailSent = 1;
          await apiService.postData('send_survey', sp, accessToken);
        } catch (err) {
          console.error(`Failed for ${ep.customerName}:`, err);
          try {
            await apiService.postData('send_survey', sp, accessToken);
          } catch (e) {
            console.error('Survey log failed:', e);
          }
        }
      }

      const { formattedSurveys, updatedStatistics } = await fetchAndFormatData();
      onSave?.({ updatedSurveys: formattedSurveys, updatedStatistics });
    } catch (err) {
      console.error('Batch error:', err);
      alert('Failed to send one or more emails.');
    }
  };

  // === JSX ===
  return (
    <div className="modal-overlay" onClick={() => setIsDropdownOpen(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Send Survey</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="form-body">
          <div className="form-row">
            {/* Customer Dropdown */}
            <div className="form-group">
              <label style={{ color: 'black' }}>Select Customer(s)</label>
              <div className="custom-dropdown" ref={dropdownRef} onClick={e => e.stopPropagation()}>
                <div className="custom-dropdown-toggle" onClick={toggleDropdown}>
                  <span>
                    {selectedCustomers.length > 0
                      ? `${selectedCustomers.length} customer(s) selected`
                      : 'Select Customer'}
                  </span>
                </div>

                <div className="custom-dropdown-menu">
                  <div className="search-container">
                    <input
                      type="text"
                      placeholder="Search by customer or email..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>

                  <div
                    className={`custom-dropdown-item select-all ${selectAll ? 'selected' : ''}`}
                    onClick={handleSelectAll}
                  >
                    <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                    Select All
                  </div>

                  {filteredCustomers.map((customer, idx) => {
                    const { email } = customerMap[customer] || {};
                    return (
                      <div
                        key={`cust-${idx}`}
                        className={`custom-dropdown-item ${selectedCustomers.includes(customer) ? 'selected' : ''}`}
                        onClick={() => toggleCustomer(customer)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCustomers.includes(customer)}
                          onChange={() => toggleCustomer(customer)}
                        />
                        <div className="contact-details">
                          <div className="contact-name">{customer}</div>
                          <div className="contact-email">{email || 'No email'}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Survey Select */}
            <div className="form-group">
              <label style={{ color: 'black' }}>Survey</label>
              <select
                value={selectedSurveyId}
                onChange={e => setSelectedSurveyId(e.target.value)}
                style={{ color: selectedSurveyId ? 'black' : 'grey' }}
              >
                <option value="">Select Survey</option>
                {surveys.map((s, i) => (
                  <option key={`s-${i}`} value={s.SurveyID}>{s.Name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Subject */}
          <div className="form-row">
            <div className="form-group full-width">
              <label>Subject</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="We’d love to hear from you about..."
              />
            </div>
          </div>

          {/* Body Preview */}
          <div className="form-row">
            <div className="form-group full-width">
              <label>Body (Preview Links)</label>
              <textarea
                value={body}
                readOnly
                rows="6"
                style={{
                  backgroundColor: '#f9f9f9',
                  resize: 'vertical',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                  whiteSpace: 'pre-wrap'
                }}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="save-btn" onClick={handleSave}>Send</button>
        </div>
      </div>
    </div>
  );
};

// PropTypes
SendDialog.propTypes = {
  project: PropTypes.object,
  contacts: PropTypes.arrayOf(PropTypes.string).isRequired,
  emails: PropTypes.arrayOf(PropTypes.string).isRequired,
  surveys: PropTypes.arrayOf(PropTypes.object).isRequired,
  customerContactIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

SendDialog.defaultProps = {
  project: {},
  contacts: [],
  emails: [],
  customerContactIds: [],
  surveys: [],
};

export default SendDialog;